import React, { useEffect, useMemo, useState } from "react";

/**
 * DataGrid
 * Props:
 *  - title?: string
 *  - rows: object[]
 *  - columns: Array<{
 *      key: string,
 *      label?: string,
 *      sortable?: boolean,
 *      exportable?: boolean, // default true
 *      render?: (value, row) => ReactNode,
 *      csvValue?: (value, row) => string | number | null | undefined // per export
 *    }>
 *  - initialHidden?: string[] // colonne inizialmente nascoste
 *  - search?: string
 *  - onSearchChange?: (v:string)=>void
 *  - filterFn?: (row, query:string)=>boolean  // se vuoi filtro custom
 *  - csvSeparator?: "," | ";"  // default ","
 */
export default function DataGrid({
  title = "Griglia",
  rows = [],
  columns = [],
  initialHidden = [],
  search,
  onSearchChange,
  filterFn,
  csvSeparator = ",",
}) {
  const [visibleKeys, setVisibleKeys] = useState(() => {
    const set = new Set(columns.map((c) => c.key));
    initialHidden.forEach((k) => set.delete(k));
    return set;
  });

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  // quando cambiano le colonne, riallinea la visibilità (mantiene le nascoste se possibile)
  useEffect(() => {
    setVisibleKeys((prev) => {
      const next = new Set();
      for (const c of columns) {
        if (prev.has(c.key)) next.add(c.key);
      }
      // se era vuoto (es. prima render), inizializza tutto visibile eccetto initialHidden
      if (next.size === 0 && columns.length) {
        const init = new Set(columns.map((c) => c.key));
        initialHidden.forEach((k) => init.delete(k));
        return init;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.map((c) => c.key).join("|")]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleKeys.has(c.key)),
    [columns, visibleKeys]
  );

  const filteredSortedRows = useMemo(() => {
    let out = Array.isArray(rows) ? [...rows] : [];

    const q = (search ?? "").trim();

    if (q && filterFn) {
      out = out.filter((r) => filterFn(r, q));
    } else if (q) {
      // default: cerca su tutte le colonne visibili
      const qq = q.toLowerCase();
      out = out.filter((r) =>
        visibleColumns.some((c) =>
          String(r?.[c.key] ?? "").toLowerCase().includes(qq)
        )
      );
    }

    if (sortKey) {
      out.sort((a, b) => {
        const av = a?.[sortKey];
        const bv = b?.[sortKey];

        if (av === null || av === undefined) return 1;
        if (bv === null || bv === undefined) return -1;

        // numerico se possibile
        const an = Number(av);
        const bn = Number(bv);
        const bothNumeric = !Number.isNaN(an) && !Number.isNaN(bn);

        let cmp = 0;
        if (bothNumeric) cmp = an - bn;
        else cmp = String(av).localeCompare(String(bv), "it", { numeric: true });

        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    return out;
  }, [rows, search, filterFn, sortKey, sortDir, visibleColumns]);

  function toggleColumn(key) {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function onHeaderClick(col) {
    if (col.sortable === false) return;

    const key = col.key;
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  }

  function sortIndicator(key) {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  function csvEscape(value) {
    if (value === null || value === undefined) return "";
    const s = String(value).replace(/"/g, '""');
    // se contiene separatore, virgolette o newline => wrap
    const needsQuotes = new RegExp(`[\\n\\r"${csvSeparator}]`).test(s);
    return needsQuotes ? `"${s}"` : s;
  }

  function exportCsv() {
    const cols = visibleColumns.filter((c) => c.exportable !== false);
    const header = cols.map((c) => csvEscape(c.label ?? c.key)).join(csvSeparator);

    const lines = filteredSortedRows.map((row) =>
      cols
        .map((c) => {
          const raw = row?.[c.key];
          const v = c.csvValue ? c.csvValue(raw, row) : raw;
          return csvEscape(v);
        })
        .join(csvSeparator)
    );

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${String(title).replace(/\s+/g, "_").toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ color: "white" }}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div style={styles.title}>{title}</div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={search ?? ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Cerca..."
            style={styles.input}
          />
          <button type="button" onClick={exportCsv} style={styles.btnPrimary} title="Esporta ciò che vedi a CSV">
            Export CSV
          </button>
        </div>
      </div>

      {/* Column visibility */}
      <div style={styles.panel}>
        <div style={styles.panelTitle}>Colonne</div>
        <div style={styles.chipsWrap}>
          {columns.map((c) => {
            const on = visibleKeys.has(c.key);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleColumn(c.key)}
                style={{
                  ...styles.chip,
                  background: on ? "rgba(59,130,246,0.18)" : "rgba(255,255,255,0.06)",
                  border: on ? "1px solid rgba(59,130,246,0.55)" : "1px solid rgba(255,255,255,0.12)",
                }}
                title={on ? "Clicca per nascondere" : "Clicca per mostrare"}
              >
                <span style={{ opacity: 0.9 }}>{on ? "👁" : "🚫"}</span>
                <span>{c.label ?? c.key}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th
                  key={c.key}
                  onClick={() => onHeaderClick(c)}
                  style={{
                    ...styles.th,
                    cursor: c.sortable === false ? "default" : "pointer",
                  }}
                  title={c.sortable === false ? "" : "Clicca per ordinare"}
                >
                  {c.label ?? c.key}
                  <span style={{ opacity: 0.85 }}>{sortIndicator(c.key)}</span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredSortedRows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, visibleColumns.length)} style={styles.empty}>
                  Nessun risultato.
                </td>
              </tr>
            ) : (
              filteredSortedRows.map((row, idx) => (
                <tr key={row?.ID ?? row?.id ?? idx} style={styles.tr}>
                  {visibleColumns.map((c) => (
                    <td key={c.key} style={styles.td}>
                      {c.render ? c.render(row?.[c.key], row) : String(row?.[c.key] ?? "")}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.footer}>
        Righe a video: <b>{filteredSortedRows.length}</b> / totali: <b>{rows.length}</b>
      </div>
    </div>
  );
}

const styles = {
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  title: { fontWeight: 800, fontSize: 16, opacity: 0.95 },
  input: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.06)",
    color: "white",
    minWidth: 260,
    outline: "none",
  },
  btnPrimary: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid rgba(59,130,246,0.65)",
    background: "linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    fontWeight: 700,
    cursor: "pointer",
  },
  panel: {
    padding: 12,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.04)",
    marginBottom: 12,
  },
  panelTitle: { fontWeight: 800, marginBottom: 8 },
  chipsWrap: { display: "flex", gap: 10, flexWrap: "wrap" },
  chip: {
    display: "inline-flex",
    gap: 8,
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: 999,
    color: "white",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
  },
  tableWrap: {
    overflow: "auto",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.12)",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: 10,
    borderBottom: "1px solid rgba(255,255,255,0.10)",
    whiteSpace: "nowrap",
    background: "rgba(255,255,255,0.04)",
    position: "sticky",
    top: 0,
    zIndex: 1,
    userSelect: "none",
  },
  td: {
    padding: 10,
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    whiteSpace: "nowrap",
  },
  tr: {
    transition: "background 0.12s ease",
  },
  empty: { padding: 14, opacity: 0.8 },
  footer: { marginTop: 10, opacity: 0.85, fontSize: 13 },
};